import { z } from 'zod';
import { PRODUCT_SIZES } from '../enums.js';
import { dateOnly, latitude, longitude, money, timeOnly } from './common.js';
import { geoJsonPolygon } from './zone.js';
/**
 * Витрина исполнителя.
 *
 * «Исполнитель» — это пользователь с ролью `courier` плюс строка в
 * `arbuz_vendors`: он и продаёт, и везёт. Роль в enum осталась прежней
 * намеренно, см. комментарий у таблицы в `server/src/db/schema.ts`.
 *
 * Два взгляда на одну сущность живут раздельно и НЕ должны сливаться:
 * `vendor` — то, что видит владелец (включая реквизиты для выплат),
 * `vendorPublic` — то, что видит клиент. Реквизиты выплат наружу не уходят
 * никогда, поэтому они физически отсутствуют во втором контракте, а не
 * вычищаются по дороге.
 */
const uzPhone = z.string().regex(/^\+998\d{9}$/, 'Ожидается номер в формате +998XXXXXXXXX');
const cardNumber = z.string().regex(/^\d{16}$/, 'Номер карты — 16 цифр без пробелов');
// ── Витрина глазами владельца ───────────────────────────────────────────────
export const vendor = z.object({
    id: z.number().int().positive(),
    display_name: z.string(),
    contact_phone: z.string().nullable(),
    payout_card_number: z.string().nullable(),
    payout_card_holder: z.string().nullable(),
    capacity_per_slot: z.number().int(),
    is_active: z.boolean(),
    /**
     * Чек-лист готовности. Витрину нельзя включить, пока чего-то не хватает,
     * и исполнитель должен видеть — чего именно, а не упираться в отказ.
     */
    products_count: z.number().int(),
    zones_count: z.number().int(),
    open_days_count: z.number().int(),
    is_ready: z.boolean(),
    /** Может ли исполнитель сам подтверждать заказы — зависит от настройки площадки. */
    can_confirm_orders: z.boolean(),
});
export const updateVendorBody = z.object({
    display_name: z.string().trim().min(1).max(120).optional(),
    contact_phone: uzPhone.nullable().optional(),
    payout_card_number: cardNumber.nullable().optional(),
    payout_card_holder: z.string().trim().max(120).nullable().optional(),
    capacity_per_slot: z.number().int().min(1).max(100).optional(),
    is_active: z.boolean().optional(),
});
// ── Витрина глазами клиента ─────────────────────────────────────────────────
export const vendorPublic = z.object({
    id: z.number().int().positive(),
    display_name: z.string(),
    /** Цена доставки по конкретному адресу — из зоны, накрывшей точку. */
    delivery_price: money,
    zone_id: z.number().int().positive(),
    /** «от N сум» на карточке выбора. null, если товара нет. */
    min_price: money.nullable(),
    products_count: z.number().int(),
    /** Работает ли сегодня и до скольки — чтобы не заставлять открывать сетку слотов. */
    works_today: z.boolean(),
    today_hours: z.object({ start: timeOnly, end: timeOnly }).nullable(),
});
export const vendorsQuery = z.object({ lat: z.coerce.number().pipe(latitude), lon: z.coerce.number().pipe(longitude) });
// ── Товар исполнителя ───────────────────────────────────────────────────────
/**
 * Названия исполнитель не вводит вовсе — ни русского, ни узбекского.
 *
 * Он выбирает ТИП товара («Арбуз» или «Дыня»), и сервер берёт оба названия из
 * категории. Форма на телефоне и человек, который карточек товара раньше не
 * заводил: два поля названия давали одно осмысленное и одно пустое.
 * `name` в запросе остаётся необязательным — им пользуется перенос данных и
 * админка, а кабинет исполнителя его не шлёт. Slug генерирует сервер.
 */
export const vendorProduct = z.object({
    id: z.number().int().positive(),
    category_id: z.number().int().positive(),
    slug: z.string(),
    name: z.string(),
    name_uz: z.string(),
    description: z.string().nullable(),
    size: z.enum(PRODUCT_SIZES).nullable(),
    price: money,
    weight_min_kg: z.string().nullable(),
    weight_max_kg: z.string().nullable(),
    image_url: z.string().nullable(),
    is_available: z.boolean(),
    sort_order: z.number().int(),
});
export const createVendorProductBody = z
    .object({
    /** Не указана — берётся первая активная категория. */
    category_id: z.number().int().positive().optional(),
    /** Не указано — название берётся из типа товара. */
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    size: z.enum(PRODUCT_SIZES).nullable().optional(),
    price: money,
    weight_min_kg: money.nullable().optional(),
    weight_max_kg: money.nullable().optional(),
    is_available: z.boolean().default(true),
    sort_order: z.number().int().min(0).max(9999).default(0),
})
    .refine((v) => !v.weight_min_kg || !v.weight_max_kg || Number(v.weight_min_kg) <= Number(v.weight_max_kg), { message: 'Минимальный вес больше максимального', path: ['weight_min_kg'] });
export const updateVendorProductBody = z.object({
    /** Смена типа переименовывает товар: имя всегда идёт за типом. */
    category_id: z.number().int().positive().optional(),
    name: z.string().trim().min(1).max(200).optional(),
    description: z.string().max(2000).nullable().optional(),
    size: z.enum(PRODUCT_SIZES).nullable().optional(),
    price: money.optional(),
    weight_min_kg: money.nullable().optional(),
    weight_max_kg: money.nullable().optional(),
    is_available: z.boolean().optional(),
    sort_order: z.number().int().min(0).max(9999).optional(),
});
// ── Зона исполнителя ────────────────────────────────────────────────────────
export const vendorZone = z.object({
    id: z.number().int().positive(),
    name: z.string(),
    delivery_price: money,
    is_active: z.boolean(),
    sort_order: z.number().int(),
    geom: geoJsonPolygon,
    /** Сколько вершин в контуре — для списка, чтобы не тащить геометрию в карточку. */
    points_count: z.number().int(),
    /**
     * Площадь контура в км². Единственная величина, по которой исполнитель
     * понимает, обвёл он квартал или район: по числу вершин это не видно.
     */
    area_km2: z.number(),
});
export const createVendorZoneBody = z.object({
    name: z.string().trim().min(1).max(120).optional(),
    delivery_price: money,
    is_active: z.boolean().default(true),
    sort_order: z.number().int().min(0).max(9999).default(0),
    geom: geoJsonPolygon,
});
export const updateVendorZoneBody = createVendorZoneBody.partial();
// ── Расписание ──────────────────────────────────────────────────────────────
/**
 * `weekday`: 0 = воскресенье … 6 = суббота — как `EXTRACT(DOW)` в Postgres
 * и `Date#getUTCDay()` в JS. Расписание передаётся ЦЕЛИКОМ, семью днями:
 * форма на телефоне не должна слать семь запросов и оказываться в
 * полусохранённом состоянии.
 */
export const vendorScheduleDay = z.object({
    weekday: z.number().int().min(0).max(6),
    work_start: timeOnly,
    work_end: timeOnly,
    is_closed: z.boolean(),
});
export const vendorScheduleResponse = z.object({ days: z.array(vendorScheduleDay) });
export const putVendorScheduleBody = z
    .object({ days: z.array(vendorScheduleDay).length(7) })
    .refine((v) => new Set(v.days.map((d) => d.weekday)).size === 7, {
    message: 'Каждый день недели должен встречаться ровно один раз',
    path: ['days'],
})
    .refine((v) => v.days.every((d) => d.is_closed || d.work_start < d.work_end), {
    message: 'Начало рабочего дня должно быть раньше конца',
    path: ['days'],
});
export const vendorDayOff = z.object({
    id: z.number().int().positive(),
    slot_date: dateOnly,
    reason: z.string().nullable(),
});
export const createVendorDayOffBody = z.object({
    slot_date: dateOnly,
    reason: z.string().max(200).nullable().optional(),
});
export const vendorDaysOffQuery = z.object({
    from: dateOnly.optional(),
    to: dateOnly.optional(),
});
// ── Админский взгляд ────────────────────────────────────────────────────────
export const adminVendor = z.object({
    id: z.number().int().positive(),
    user_id: z.number().int().positive(),
    display_name: z.string(),
    contact_phone: z.string().nullable(),
    payout_card_number: z.string().nullable(),
    payout_card_holder: z.string().nullable(),
    capacity_per_slot: z.number().int(),
    is_active: z.boolean(),
    owner_name: z.string().nullable(),
    owner_is_blocked: z.boolean(),
    products_count: z.number().int(),
    zones_count: z.number().int(),
    orders_count: z.number().int(),
});
export const createAdminVendorBody = z.object({
    telegram_id: z.number().int().positive(),
    display_name: z.string().trim().min(1).max(120),
    first_name: z.string().trim().min(1).max(100).optional(),
    phone: uzPhone.optional(),
});
export const updateAdminVendorBody = z.object({
    display_name: z.string().trim().min(1).max(120).optional(),
    capacity_per_slot: z.number().int().min(1).max(100).optional(),
    is_active: z.boolean().optional(),
});
//# sourceMappingURL=vendor.js.map